/**
 * fetchFieldRecords (Inventory Screens Step 5.1) — per-site records fan-out.
 *
 * Walks get_field_records' continuation paging for one site and returns the
 * accumulated array of page responses — exactly the shape
 * persistLoadResponse's recordsResp already accepts (array of pages).
 *
 * Paging contract (Step 2 Load): POST {site_id, page_size, continuation_token?}
 * → {site, records, continuation_token, has_more}. WALK UNTIL has_more===false
 * — never count math; a trailing empty page is legal. caller_email rides the
 * body (resolve_principal fallback reads params then body).
 *
 * Transport rides apiFetch (Bearer when acquirable + caller_email fallback);
 * the fetch itself is try/caught — apiFetch's never-throws contract is
 * token-scoped only, a dead network REJECTS.
 */

import { apiFetch } from '../../../auth/apiFetch';
import { persistLoadResponse, getFieldSnapshot, getRecords, getQueuedRecordIds, isDirtyRecord } from '../db/fieldStore';
import { isPendingRecord } from '../sync/pendingId';
import { isCompleteWalk, clearPruneDryRun, reportPruneDryRun } from './pruneDryRun';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';
const PAGE_SIZE = 300; // server default; hard cap 500

/**
 * @param {string} siteId
 * @param {string|null} callerEmail
 * @returns {Promise<Object[]>} array of raw page responses, request order
 */
export async function fetchFieldRecords(siteId, callerEmail) {
  const pages = [];
  let continuationToken = null;

  for (;;) {
    const body = {
      site_id: siteId,
      page_size: PAGE_SIZE,
      caller_email: callerEmail || null,
    };
    if (continuationToken) body.continuation_token = continuationToken;

    let res;
    try {
      res = await apiFetch(`${API_BASE_URL}/get_field_records`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch (netErr) {
      throw new Error(`get_field_records transport failure: ${netErr.message}`);
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `get_field_records failed: ${res.status}`);
    }

    const page = await res.json();
    pages.push(page);

    if (page.has_more !== true) break; // the ONLY exit condition — never count math
    continuationToken = page.continuation_token || null;
    if (!continuationToken) break; // defensive: has_more without a token can't continue
  }

  return pages;
}

/**
 * Single-record point-read (Path 2 re-apply seam — rides the Phase 1b
 * record_id arm of get_field_records). Same envelope as the paged Load, but
 * the server guarantees continuation_token null / has_more false, so there
 * is no walk. Transport and caller_email ride identically to
 * fetchFieldRecords above (apiFetch + body fallback).
 *
 * @param {string} siteId
 * @param {string} recordId — the Cosmos doc id (row pk), NOT tool_id
 * @param {string|null} callerEmail
 * @returns {Promise<Object|null>} the one field-shape record (children +
 *   v14 keys + etag hoisted, byte-identical to a Load record), or null when
 *   records comes back empty (not-found / purged server-side) — the caller
 *   decides what that means; this never throws on empty.
 */
export async function fetchFieldRecord(siteId, recordId, callerEmail) {
  let res;
  try {
    res = await apiFetch(`${API_BASE_URL}/get_field_records`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        site_id: siteId,
        record_id: recordId,
        caller_email: callerEmail || null,
      }),
    });
  } catch (netErr) {
    throw new Error(`get_field_records transport failure: ${netErr.message}`);
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `get_field_records failed: ${res.status}`);
  }
  const page = await res.json();
  const records = (page && page.records) || [];
  return records.length ? records[0] : null;
}

/**
 * Fetch a site's records and persist them RECORDS-ONLY, then read the stored
 * rows back from IDB (what the UI renders — proves the persist path).
 *
 * Records-only persist: persistLoadResponse writes the snapshot row
 * unconditionally, so a bare call would null the offline identity/profile.
 * The current snapshot is echoed back through the call instead — sites get a
 * no-op bulkPut([]), identity/profile re-persist unchanged, and the
 * dirty-preserving mergeRecord (4.3c-ii) routes every record row.
 *
 * Stored rows the fetch did NOT return (leftovers — e.g. a tool soft-deleted
 * server-side, which get_field_records no longer returns) are appended after
 * the fetched rows, pk order, ONLY if at least one keep-condition holds:
 *   1. record_id starts with 'pending-' (created offline, not yet on server)
 *   2. dirty is non-empty (unsent edits — isDirtyRecord)
 *   3. any syncQueue item references its record_id, in ANY status
 *   4. site_id differs from the loaded site (defensive: getRecords filters by
 *      site_id, so this cannot match today)
 * A leftover matching none of the four is EXCLUDED FROM THE RETURNED ARRAY
 * ONLY — it is not deleted from IndexedDB; it simply stops being rendered.
 * If the queue cannot be read, every leftover is kept (a read failure must
 * never cause a drop).
 *
 * Ghost prune Phase A1 (dry run): the excluded leftovers are reported through
 * pruneDryRun.js as what a prune WOULD delete — only when the page walk is
 * proved complete and the queue was read — and are still not deleted.
 *
 * @returns {Promise<Object[]>} the site's stored record rows, LOAD order —
 *   getRecords() alone returns pk (record_id) index order, so the rows are
 *   re-sequenced by the fetched page order; kept leftovers follow, pk order.
 */
export async function loadSiteRecords(siteId, callerEmail) {
  clearPruneDryRun(); // a Load that throws must not leave the last one's count up
  const pages = await fetchFieldRecords(siteId, callerEmail);
  const snap = await getFieldSnapshot();
  await persistLoadResponse({
    recordsResp: pages,
    identity: snap?.identity ?? null,
    profile: snap?.profile ?? null,
  });
  const stored = await getRecords(siteId);
  const byId = new Map(stored.map((r) => [r.record_id, r]));
  const ordered = [];
  for (const page of pages) {
    for (const rec of page.records || []) {
      const row = byId.get(rec.id);
      if (row) {
        ordered.push(row);
        byId.delete(rec.id);
      }
    }
  }
  // Leftovers: keep only rows that still carry work. Read the queue ONCE;
  // on a read failure keep everything (current behaviour) — never drop blind.
  let queuedIds = null;
  try {
    queuedIds = await getQueuedRecordIds();
  } catch (err) {
    console.warn('loadSiteRecords: could not read syncQueue — keeping all leftover rows', err);
  }
  // Ghost prune Phase A1, DRY RUN: a leftover that fails the keep test is what
  // a prune would delete. It is collected and reported, and stays in IndexedDB.
  const pruneCandidates = [];
  for (const leftover of byId.values()) {
    const keep = queuedIds === null
      || isPendingRecord(leftover.record_id || '')
      || isDirtyRecord(leftover)
      || queuedIds.has(leftover.record_id)
      || leftover.site_id !== siteId;
    if (keep) ordered.push(leftover);
    else pruneCandidates.push(leftover);
  }
  reportPruneDryRun(siteId, {
    complete: isCompleteWalk(pages),
    queueRead: queuedIds !== null,
    candidates: pruneCandidates,
    storedCount: stored.length,
  });
  return ordered;
}
